import React, { useState, useEffect, useCallback } from 'react';
import { Layers, BarChart2, ScrollText } from 'lucide-react';
import GoalsSummaryCard from './widgets/GoalsSummaryCard';
import PlaybookAnalytics from './PlaybookAnalytics';
import PlaybookSelector from './widgets/PlaybookSelector';
import PlaybookDetail from './widgets/PlaybookDetail';
import PlaybookModal from './widgets/PlaybookModal';
import RuleModal from './widgets/RuleModal';
import StrategyPlaceholder from './widgets/StrategyPlaceholder';
import {
  getPlaybooks,
  createPlaybook,
  updatePlaybook,
  deletePlaybook,
  getRules,
  createRule,
  updateRule,
  deleteRule,
} from '../../services/playbookService';
import { trackPlaybookCreated, trackPlaybookRuleAdded, trackPlaybookAnalyticsViewed } from '../../services/analytics';

const CTXPlaybook = () => {
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rulesLoading, setRulesLoading] = useState(false);

  // Modal state
  const [playbookModalOpen, setPlaybookModalOpen] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmPlaybook, setDeleteConfirmPlaybook] = useState(false);

  const selectedPlaybook = playbooks.find((p) => p.id === selectedId) || null;
  const [activeSection, setActiveSection] = useState('playbook');

  // Load playbooks
  const loadPlaybooks = useCallback(async () => {
    try {
      const res = await getPlaybooks();
      const pbs = res.playbooks || [];
      setPlaybooks(pbs);
      // Auto-select first if none selected
      if (pbs.length > 0 && (!selectedId || !pbs.find((p) => p.id === selectedId))) {
        setSelectedId(pbs[0].id);
      }
    } catch (err) {
      console.error('Failed to load playbooks:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  // Load rules for selected playbook
  const loadRules = useCallback(async () => {
    if (!selectedId) {
      setRules([]);
      return;
    }
    setRulesLoading(true);
    try {
      const res = await getRules(selectedId);
      setRules(res.rules || []);
    } catch (err) {
      console.error('Failed to load rules:', err);
    } finally {
      setRulesLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    loadPlaybooks();
  }, []); // eslint-disable-line

  useEffect(() => {
    loadRules();
  }, [selectedId, loadRules]);

  // ─── Playbook handlers ────────────────────────────────

  const handleCreatePlaybook = async ({ name, description }) => {
    setSaving(true);
    try {
      const res = await createPlaybook({ name, description });
      trackPlaybookCreated();
      setPlaybookModalOpen(false);
      setEditingPlaybook(null);
      await loadPlaybooks();
      setSelectedId(res.playbook.id);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePlaybook = async ({ name, description }) => {
    if (!editingPlaybook) return;
    setSaving(true);
    try {
      await updatePlaybook(editingPlaybook.id, { name, description });
      setPlaybookModalOpen(false);
      setEditingPlaybook(null);
      await loadPlaybooks();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlaybook = async () => {
    if (!selectedId) return;
    if (!deleteConfirmPlaybook) {
      setDeleteConfirmPlaybook(true);
      setTimeout(() => setDeleteConfirmPlaybook(false), 3000);
      return;
    }
    try {
      await deletePlaybook(selectedId);
      setDeleteConfirmPlaybook(false);
      setSelectedId(null);
      await loadPlaybooks();
    } catch (err) {
      alert(err.message);
    }
  };

  // ─── Rule handlers ────────────────────────────────────

  const handleCreateRule = async ({ label, description }) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await createRule(selectedId, { label, description });
      trackPlaybookRuleAdded();
      setRuleModalOpen(false);
      setEditingRule(null);
      await loadRules();
      await loadPlaybooks(); // refresh rule counts
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRule = async ({ label, description }) => {
    if (!selectedId || !editingRule) return;
    setSaving(true);
    try {
      await updateRule(selectedId, editingRule.id, { label, description });
      setRuleModalOpen(false);
      setEditingRule(null);
      await loadRules();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId, isActive) => {
    if (!selectedId) return;
    try {
      await updateRule(selectedId, ruleId, { is_active: isActive });
      await loadRules();
      await loadPlaybooks(); // refresh rule counts
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!selectedId) return;
    try {
      await deleteRule(selectedId, ruleId);
      await loadRules();
      await loadPlaybooks(); // refresh rule counts
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-muted rounded w-1/4" />
            <div className="h-32 bg-muted rounded" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" />
            <h1 className="text-xl font-bold text-foreground">Blueprint</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Engineer your trading system
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-border">
          {[
            { id: 'playbook', label: 'My Playbooks', icon: ScrollText },
            { id: 'analytics', label: 'Analytics', icon: BarChart2 },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => {
                setActiveSection(id);
                if (id === 'analytics') trackPlaybookAnalyticsViewed();
              }}
              className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeSection === id
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
              {activeSection === id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
              )}
            </button>
          ))}
        </div>

        {/* Analytics section */}
        {activeSection === 'analytics' && (
          <PlaybookAnalytics playbooks={playbooks} />
        )}

        {/* Playbook management section */}
        {activeSection === 'playbook' && (
          <>
            {/* Goals */}
            <GoalsSummaryCard />

        {/* Playbooks */}
        {playbooks.length === 0 ? (
          <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border p-8 text-center">
            <ScrollText className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-foreground mb-1">Create Your First Playbook</h3>
            <p className="text-sm text-muted-foreground mb-4">
              A playbook contains your trading rules. Create one to start defining the rules you follow.
            </p>
            <button
              onClick={() => { setEditingPlaybook(null); setPlaybookModalOpen(true); }}
              className="px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Create Playbook
            </button>
          </div>
        ) : (
          <>
            <PlaybookSelector
              playbooks={playbooks}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onCreateNew={() => { setEditingPlaybook(null); setPlaybookModalOpen(true); }}
            />

            {rulesLoading ? (
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-8 bg-muted rounded" />
                  <div className="h-8 bg-muted rounded" />
                  <div className="h-8 bg-muted rounded" />
                </div>
              </div>
            ) : (
              <PlaybookDetail
                playbook={selectedPlaybook}
                rules={rules}
                onEditPlaybook={() => { setEditingPlaybook(selectedPlaybook); setPlaybookModalOpen(true); }}
                onDeletePlaybook={handleDeletePlaybook}
                onAddRule={() => { setEditingRule(null); setRuleModalOpen(true); }}
                onEditRule={(rule) => { setEditingRule(rule); setRuleModalOpen(true); }}
                onToggleRule={handleToggleRule}
                onDeleteRule={handleDeleteRule}
              />
            )}
          </>
        )}

        {/* Strategy placeholder */}
        <StrategyPlaceholder />
          </>
        )}
      </div>

      {/* Modals */}
      {playbookModalOpen && (
        <PlaybookModal
          playbook={editingPlaybook}
          onSave={editingPlaybook ? handleUpdatePlaybook : handleCreatePlaybook}
          onClose={() => { setPlaybookModalOpen(false); setEditingPlaybook(null); }}
          saving={saving}
        />
      )}

      {ruleModalOpen && (
        <RuleModal
          rule={editingRule}
          onSave={editingRule ? handleUpdateRule : handleCreateRule}
          onClose={() => { setRuleModalOpen(false); setEditingRule(null); }}
          saving={saving}
        />
      )}
    </div>
  );
};

export default CTXPlaybook;
